import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CardMedia, Input, TextField, Button, MenuItem, Select, FormHelperText } from "@mui/material";
import { useState, ChangeEvent } from "react";
import CloseIcon from '@mui/icons-material/Close';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UploadMedia } from "../../utils/uploadMedia";
import { showError, showToast } from "../../helpers";
import { useAddSubAdminMutation, useLazySubAdminByIdQuery, useUpdateSubAdminMutation } from "../../services/subAdmin";
import { useLazyGetAllRolesQuery, RoleResponse } from "../../services/accessRole";
import usePermission from "../../hooks/userAccess";

const AddSubadmin = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { isAdd } = usePermission({
    label: 'Manage Sub-Admin',
    page_permission: 'isAdd',
  })

  const [image, setImage] = useState<string>("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [roles, setRoles] = useState<RoleResponse[]>([]);

  // Formik setup
  const formik = useFormik({
    initialValues: {
      fullName: "",
      image: "",
      accessRole: "",
      email: "",
      phoneNo: "",
      dialCode: "+971",
    },
    validationSchema: Yup.object({
      fullName: Yup.string().trim().required("Full name is required"),
      image: Yup.string(),
      accessRole: Yup.string().required("Role is required"),
      email: Yup.string().email("Enter a valid email").required("Email is required"),
      phoneNo: Yup.string()
        .matches(/^[0-9]{5,15}$/g, "Enter a valid phone number")
        .required("Phone number is required"),
      dialCode: Yup.string().required("Dial code is required"),
    }),
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      try {
        const body = {
          fullName: values.fullName,
          image: values.image,
          accessRole: values.accessRole,
          email: values.email,
          phoneNo: values.phoneNo,
          dialCode: values.dialCode,
        } as any;

        if (id) {
          const res = await updateSubAdmin({ id, body }).unwrap();
          if ((res as any)?.statusCode === 200) {
            showToast("Sub-Admin updated successfully");
            navigate(-1);
          }
        } else {
          const res = await addSubAdmin(body).unwrap();
          if ((res as any)?.statusCode === 200) {
            showToast("Sub-Admin added successfully");
            navigate(-1);
          }
        }
      } catch (error: any) {
        showError(error?.message || error?.data?.message || "Failed to save sub-admin");
      }
    },
  });

  // Handle image upload
  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    try {
      if (!file) {
        setImage("");
        formik.setFieldValue("image", "");
        return;
      }
      const res = await UploadMedia(file);
      const imageUrl = res?.data?.images?.[0]?.url || res?.data?.image || "";
      if (imageUrl) {
        setImage(imageUrl);
        formik.setFieldValue("image", imageUrl);
        showToast("Image uploaded successfully");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      showError("Image upload failed");
    }
  };

  // Handle phone input
  const handleChangePhone = (phone: string, country: any) => {
    formik.setFieldValue("phoneNo", phone.replace(country.dialCode, ""));
    formik.setFieldValue("dialCode", country.dialCode);
    setPhoneDisplay(phone);
  };

  // API hooks
  const [addSubAdmin, { isLoading: isAdding }] = useAddSubAdminMutation();
  const [updateSubAdmin, { isLoading: isUpdating }] = useUpdateSubAdminMutation();
  const [getSubAdminById] = useLazySubAdminByIdQuery();
  const [triggerGetAllRoles, { isFetching }] = useLazyGetAllRolesQuery();

  // Prefill on edit
  React.useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return;
      try {
        const res: any = await getSubAdminById({ id }).unwrap();
        const data = res?.data || {};
        const fullName = data?.fullName || data?.name || "";
        const email = data?.email || "";
        const accessRole = data?.accessRole?._id || data?.roleId || "";
        const dial = (data?.dialCode || "").toString();
        const dialWithPlus = dial.startsWith("+") ? dial : dial ? `+${dial}` : "";
        const phoneNo = data?.phoneNo || data?.phone || "";
        const imageUrl = data?.image || data?.profile_picture || "";

        formik.setValues({
          fullName,
          email,
          accessRole,
          dialCode: dialWithPlus || formik.values.dialCode,
          phoneNo,
          image: imageUrl,
        });
        if (imageUrl) setImage(imageUrl);
        const compPhone = `${(dialWithPlus || formik.values.dialCode).replace("+", "")}${phoneNo}`;
        setPhoneDisplay(compPhone);
      } catch (error: any) {
        showError(error?.message || error?.data?.message || "Failed to fetch sub-admin details");
      }
    };
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch roles for dropdown (page=1, limit=100)
  React.useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res: any = await triggerGetAllRoles({ skip: 0, limit: 100, search: "" }).unwrap();
        const data = res?.data ?? res;
        const list = data?.SubAdminAccess ?? data?.list ?? data?.items ?? data?.rows ?? data;
        if (Array.isArray(list)) setRoles(list as RoleResponse[]);
      } catch (e) {
        setRoles([]);
      }
    };
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="main_title">
        <h1>{id ? "Edit Sub-Admin" : "Add Sub-Admin"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {' '} - <button onClick={() => navigate(-1)}>Manage Sub-Admin</button>
          {' '} - {id ? "Edit Sub-Admin" : "Add Sub-Admin"}
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_100">
              <label>Upload Image</label>
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="photo" />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        formik.setFieldValue("image", "");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={handleImageUpload}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img
                          src="/static/images/edit2_icon.svg"
                          alt="icon"
                        />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
              {(formik.touched.image || formik.submitCount > 0) && formik.errors.image && (
                <FormHelperText error>{formik.errors.image}</FormHelperText>
              )}
            </div>

            <div className="control_group w_33">
              <label htmlFor="name">Name</label>
              <TextField
                id="name"
                placeholder="Enter Name"
                hiddenLabel
                fullWidth
                value={formik.values.fullName}
                onChange={(e) => formik.setFieldValue("fullName", e.target.value)}
                onBlur={() => formik.setFieldTouched("fullName", true)}
                error={(formik.touched.fullName || formik.submitCount > 0) && Boolean(formik.errors.fullName)}
                helperText={(formik.touched.fullName || formik.submitCount > 0) && formik.errors.fullName}
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="email">Email Id</label>
              <TextField
                id="email"
                placeholder="Enter Email Id"
                hiddenLabel
                fullWidth
                value={formik.values.email}
                onChange={(e) => formik.setFieldValue("email", e.target.value)}
                onBlur={() => formik.setFieldTouched("email", true)}
                error={(formik.touched.email || formik.submitCount > 0) && Boolean(formik.errors.email)}
                helperText={(formik.touched.email || formik.submitCount > 0) && formik.errors.email}
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="phone">Phone Number</label>
              <PhoneInput
                country={"ae"}
                placeholder="Enter Phone Number"
                enableSearch={false}
                autoFormat={false}
                inputStyle={{ width: "100%" }}
                value={phoneDisplay}
                onChange={handleChangePhone}
                onBlur={formik.handleBlur}
              />
              {(formik.touched.phoneNo || formik.submitCount > 0) && formik.errors.phoneNo && (
                <FormHelperText error>{formik.errors.phoneNo}</FormHelperText>
              )}
            </div>

            <div className="control_group w_33">
              <label htmlFor="role">Role</label>
              <Select
                id="role"
                fullWidth
                value={formik.values.accessRole || "default"}
                onChange={(e) => formik.setFieldValue("accessRole", e.target.value)}
                onBlur={() => formik.setFieldTouched("accessRole", true)}
              >
                <MenuItem disabled value="default">
                  Select Role
                </MenuItem>
                {roles?.map((r) => (
                  <MenuItem key={r?._id} value={r?._id}>{r?.name || '-'}</MenuItem>
                ))}
              </Select>
              {(formik.touched.accessRole || formik.submitCount > 0) && formik.errors.accessRole && (
                <FormHelperText error>{formik.errors.accessRole}</FormHelperText>
              )}
            </div>
          </div>

          {isAdd && <div className="form_btn">
            <Button className="btn btn_primary" type="submit" disabled={isAdding || isUpdating}>
              {id ? (isUpdating ? "Updating..." : "Update") : (isAdding ? "Saving..." : "Save")}
            </Button>
          </div>}
        </form>
      </div>
    </>
  );
};

export default AddSubadmin;
